import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from 'react-dom';
import ReactCountryFlag from "react-country-flag";
import { countryToAlpha2 } from 'country-to-iso';
import Modal from 'react-bootstrap/Modal';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import "./PreviewProfileModal.css";

const INTERESTS = [
  { emoji: "🎶", label: "Music" },
  { emoji: "🍳", label: "Cooking" },
  { emoji: "🎨", label: "Art & Design" },
  { emoji: "🎥", label: "Movies & Series" },
  { emoji: "📚", label: "Books & Reading" },
  { emoji: "🌍", label: "Traveling" },
  { emoji: "🍷", label: "Wine & Drinks" },
  { emoji: "🧘", label: "Yoga & Meditation" },
  { emoji: "⚽", label: "Sports" },
  { emoji: "🎭", label: "Theater & Culture" },
  { emoji: "🧩", label: "Board Games" },
  { emoji: "🐾", label: "Animals & Pets" },
  { emoji: "🍰", label: "Baking" },
  { emoji: "💬", label: "Deep Conversations" },
  { emoji: "😂", label: "Humor & Memes" },
  { emoji: "📷", label: "Photography" },
  { emoji: "🕯️", label: "Shabbat Traditions" },
  { emoji: "👩‍🍳", label: "Hosting" },
  { emoji: "🌿", label: "Nature & Hiking" },
  { emoji: "🧠", label: "Psychology & People" },
  { emoji: "💻", label: "Tech & Startups" },
  { emoji: "☕", label: "Coffee Lovers" },
  { emoji: "🎤", label: "Singing & Karaoke" },
  { emoji: "💡", label: "Ideas & Innovation" }
];

const EnlargedImage = ({ image, onClose, isProfile = false, onPrev, onNext, hasNav }) => {
  const handleKeyDown = useCallback((e) => {
    if (hasNav) {
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    }
    if (e.key === 'Escape') onClose();
  }, [hasNav, onPrev, onNext, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!image) return null;

  return ReactDOM.createPortal(
    <div className="enlarged-image-overlay" onClick={onClose}>
      <div className="enlarged-image-wrapper">
        <div className={`enlarged-image-container ${isProfile ? 'profile' : ''}`}>
          <img
            src={image}
            alt="Enlarged"
            className="enlarged-image"
            onClick={e => e.stopPropagation()}
          />
        </div>
        {hasNav && (
          <>
            <button
              className="enlarged-image-nav prev"
              onClick={(e) => { e.stopPropagation(); onPrev(); }}
            >
              ‹
            </button>
            <button
              className="enlarged-image-nav next"
              onClick={(e) => { e.stopPropagation(); onNext(); }}
            >
              ›
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default function PreviewProfileModal({ open, onClose, profile }) {
  const [showEnlargedImage, setShowEnlargedImage] = useState(false);
  const [enlargedGalleryIndex, setEnlargedGalleryIndex] = useState(null);
  const [reviews, setReviews] = useState([]);

  // Get country code from country name (same as HostCard)
  const getCountryCodeSafe = (country) => {
    if (!country) return '';
    const code = countryToAlpha2(country);
    console.log('PreviewProfileModal - Country:', country, 'Code:', code); // Debug log
    return code || '';
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    setShowEnlargedImage(true);
  };

  const handleGalleryImageClick = (index) => {
    setEnlargedGalleryIndex(index);
  };

  const handlePrevImage = () => {
    if (!profile.gallery || !profile.gallery.length) return;
    setEnlargedGalleryIndex((prev) =>
      prev === 0 ? profile.gallery.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    if (!profile.gallery || !profile.gallery.length) return;
    setEnlargedGalleryIndex((prev) =>
      prev === profile.gallery.length - 1 ? 0 : prev + 1
    );
  };

  useEffect(() => {
    const fetchReviews = async () => {
      if (!profile || !profile.uid) return;
      const fetchedReviews = [];
      const userCache = {};
      const getUserData = async (uid) => {
        if (!uid) return null;
        if (userCache[uid]) return userCache[uid];
        const userRef = doc(db, 'users', uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          userCache[uid] = data;
          return data;
        }
        return null;
      };
      try {
        // Feedbacks where user was a guest (hostFeedback or hostReview)
        const hostFeedbacksQuery = query(
          collection(db, 'dinnerRequests'),
          where('userId', '==', profile.uid),
        );
        const hostFeedbacksSnapshot = await getDocs(hostFeedbacksQuery);
        for (const docSnap of hostFeedbacksSnapshot.docs) {
          const data = docSnap.data();
          // New field
          if (data.hostFeedback && data.hostFeedback.text) {
            const author = await getUserData(data.hostFeedback.authorId);
            let authorName = 'Anonymous';
            if (author) {
              if (author.firstName || author.lastName) {
                authorName = `${author.firstName || ''} ${author.lastName || ''}`.trim();
              } else if (author.name) {
                authorName = author.name;
              }
            }
            fetchedReviews.push({
              text: data.hostFeedback.text,
              author: authorName
            });
          }
          // Old field
          if (data.hostReview && data.hostReview.text) {
            const author = await getUserData(data.hostReview.authorId);
            let authorName = 'Anonymous';
            if (author) {
              if (author.firstName || author.lastName) {
                authorName = `${author.firstName || ''} ${author.lastName || ''}`.trim();
              } else if (author.name) {
                authorName = author.name;
              }
            }
            fetchedReviews.push({
              text: data.hostReview.text,
              author: authorName
            });
          }
        }
        // Feedbacks where user was a host (guestFeedback or guestReview)
        const guestFeedbacksQuery = query(
          collection(db, 'dinnerRequests'),
          where('hostId', '==', profile.uid),
        );
        const guestFeedbacksSnapshot = await getDocs(guestFeedbacksQuery);
        for (const docSnap of guestFeedbacksSnapshot.docs) {
          const data = docSnap.data();
          // New field
          if (data.guestFeedback && data.guestFeedback.text) {
            const author = await getUserData(data.guestFeedback.authorId);
            let authorName = 'Anonymous';
            if (author) {
              if (author.firstName || author.lastName) {
                authorName = `${author.firstName || ''} ${author.lastName || ''}`.trim();
              } else if (author.name) {
                authorName = author.name;
              }
            }
            fetchedReviews.push({
              text: data.guestFeedback.text,
              author: authorName
            });
          }
          // Old field
          if (data.guestReview && data.guestReview.text) {
            const author = await getUserData(data.guestReview.authorId);
            let authorName = 'Anonymous';
            if (author) {
              if (author.firstName || author.lastName) {
                authorName = `${author.firstName || ''} ${author.lastName || ''}`.trim();
              } else if (author.name) {
                authorName = author.name;
              }
            }
            fetchedReviews.push({
              text: data.guestReview.text,
              author: authorName
            });
          }
        }
        setReviews(fetchedReviews);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };
    if (open) fetchReviews();
  }, [profile, open]);

  // גלילה אוטומטית לאזור הפידבקים אם יש עוגן #feedbacks ב-URL
  useEffect(() => {
    if (!open) return;
    if (window.location.hash === '#feedbacks') {
      setTimeout(() => {
        const feedbacksSection = document.getElementById('profile-preview-feedbacks-section');
        if (feedbacksSection) {
          feedbacksSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300); // לחכות שהמודל יפתח
    }
  }, [open]);

  return (
    <>
      <Modal show={open} onHide={onClose} centered size="lg" dialogClassName="profile-preview-bootstrap-modal" style={{ zIndex: 100000 }}>
        <Modal.Body style={{ fontFamily: 'Inter, sans-serif', padding: 0 }}>
          <button className="profile-preview-close-btn" onClick={onClose} style={{ position: 'absolute', top: 18, right: 24, zIndex: 10, background: 'none', border: 'none', fontSize: 28, cursor: 'pointer' }}>×</button>
          <div style={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: 0, marginBottom: 24, marginTop: 24 }}>
            <img
              src={profile.avatar || '/blankprofile.png'}
              alt="avatar"
              className="profile-preview-avatar"
              style={{ cursor: 'pointer', marginRight: 20, marginLeft: 0 }}
              onClick={handleImageClick}
            />
            <div className="profile-preview-title-col" style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <span
                className="profile-preview-name"
                style={{ cursor: 'pointer' }}
                onClick={handleImageClick}
              >
                {profile.name}
              </span>
              <span className="profile-preview-location" style={{ marginTop: 2 }}>
                {profile.city && <>{profile.city}, </>}{profile.country}
                {profile.country && (
                  <ReactCountryFlag
                    countryCode={getCountryCodeSafe(profile.country)}
                    svg
                    style={{ fontSize: '1em', marginLeft: 8, marginRight: 2, verticalAlign: 'middle' }}
                  />
                )}
              </span>
            </div>
          </div>

          {(profile.funFact || profile.shabbotAnswers?.funFact) && profile.name && (
            <div className="profile-preview-section fun-fact-section">
              <strong>{profile.name}'s fun fact:</strong>
              <span style={{ fontStyle: 'italic', marginLeft: '5px' }}>
                "{profile.funFact || profile.shabbotAnswers?.funFact}"
              </span>
            </div>
          )}

          <div className="profile-preview-section">
            <div className="profile-preview-section-title">About</div>
            <div className="profile-preview-section-content">{profile.bio}</div>
          </div>

          <div style={{ width: '100%', height: '1px', background: '#bfc7d1', margin: '8px 0 18px 0' }} />

          <div className="profile-preview-section">
            <div className="profile-preview-section-title">Hosts details</div>
            <div style={{marginBottom:12}}>
              <b>Languages spoken:</b> {profile.languages || ''}
            </div>
            <div>
              <b>Interests:</b>
              <div className="interests-grid2" style={{marginTop: 6}}>
                {profile.interests && profile.interests.split(',').map(s => s.trim()).filter(Boolean).map(label => {
                  const found = INTERESTS.find(i => i.label === label);
                  return found ? (
                    <span key={label} className="interest-badge2">
                      <span className="emoji">{found.emoji}</span> {found.label}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          </div>

          <div className="profile-preview-section">
            <div className="profile-preview-section-title">Gallery</div>
            <div className="profile-preview-gallery">
              {profile.gallery && profile.gallery.length > 0 ? (
                profile.gallery.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`gallery-${i}`}
                    className="profile-preview-gallery-img"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleGalleryImageClick(i)}
                  />
                ))
              ) : (
                <span style={{ color: '#888', fontSize: '0.95em' }}>No photos yet</span>
              )}
            </div>
          </div>

          {reviews.length > 0 && (
            <>
              <div style={{ width: '100%', height: '1px', background: '#bfc7d1', margin: '8px 0 18px 0' }} />
              <div className="profile-preview-section" id="profile-preview-feedbacks-section">
                <div className="profile-preview-section-title">Feedbacks</div>
                <div className="profile-preview-reviews-list">
                  {reviews.map((review, index) => (
                    <div key={index} className="review-item">
                      <p className="review-text">"{review.text}"</p>
                      <p className="review-author" style={{textAlign: 'left', marginTop: 2}}>- {review.author} -</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <EnlargedImage
        image={showEnlargedImage ? profile.avatar : null}
        onClose={() => setShowEnlargedImage(false)}
        isProfile={true}
      />

      <EnlargedImage
        image={enlargedGalleryIndex !== null ? profile.gallery[enlargedGalleryIndex] : null}
        onClose={() => setEnlargedGalleryIndex(null)}
        onPrev={handlePrevImage}
        onNext={handleNextImage}
        hasNav={true}
      />
    </>
  );
} 